import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, vModelSelect as _vModelSelect, withDirectives as _withDirectives, createCommentVNode as _createCommentVNode, vModelText as _vModelText, normalizeClass as _normalizeClass, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, withModifiers as _withModifiers } from "vue"

const _hoisted_1 = { class: "formulario-modelo" }
const _hoisted_2 = { class: "form-group" }
const _hoisted_3 = { class: "select-con-boton" }
const _hoisted_4 = ["value"]
const _hoisted_5 = ["value"]
const _hoisted_6 = {
  key: 0,
  class: "input-nuevo"
}
const _hoisted_7 = { class: "form-group" }
const _hoisted_8 = { class: "form-group" }
const _hoisted_9 = ["max"]
const _hoisted_10 = { class: "form-group" }
const _hoisted_11 = { class: "rango-vigencia" }
const _hoisted_12 = { class: "form-group" }
const _hoisted_13 = { class: "form-group" }
const _hoisted_14 = { class: "escala-toggle" }
const _hoisted_15 = { class: "form-group" }
const _hoisted_16 = {
  key: 0,
  class: "niveles-listado"
}
const _hoisted_17 = ["for"]
const _hoisted_18 = ["id", "onUpdate:modelValue"]
const _hoisted_19 = { class: "botones" }
const _hoisted_20 = ["disabled"]
const _hoisted_21 = ["disabled"]

import { ref, Ref, onMounted, computed } from 'vue'
import useModelo from '@/store/GestionModelo'
import { useGestionTipoModelos } from '@/store/GestionTipoModelo'
import router from '@/router'
import type EstructuraModelo from '@/interfaces/EstructuraModelo'
import MensajeFlotante from '@/components/MensajeFlotante.vue'


export default /*@__PURE__*/_defineComponent({
  __name: 'GestionView',
  setup(__props) {

/**
 * Alta y listado de modelos de calidad.
 *
 * El modelo es la plantilla general: aquí no se define área ni ámbito, eso llega
 * al crear una ejecución.
 *
 * ── Enrutado ─────────────────────────────────────────────────────
 *   `Gestion` (CrearModelo) — permiso `modelos.crear`
 *
 * Vista enrutada. Obtiene sus datos a través de stores Pinia: no llama a `http`
 * ni a los servicios directamente.
 */

const nombreModelo: Ref<string> = ref('')
const escalaMax = ref<5 | 10>(5)
const cantidadNiveles: Ref<number> = ref(0)
const niveles: Ref<string[]> = ref([])
const store = useModelo()
const tipoModeloStore = useGestionTipoModelos()
const tipoSeleccionado = ref<string>('')
const feedback = ref('')
const loading = ref(false)
const submitted = ref(false)
const mensajeVisible = ref(false)
const mensajeTexto = ref('')
const mensajeTipo = ref<'positivo' | 'error'>('positivo')
const tiposTemporal = ref<string[]>([])
const mostrarInputNuevoTipo = ref(false)
const nuevoTipoNombre = ref('')
const fechaCreacion = ref<string>(hoyFormatoISODate())
const fechaMaxima = computed(() => hoyFormatoISODate())
// Vigencia normativa (rango de años) y acuerdo que adopta el modelo.
const vigenciaInicio = ref<number | null>(null)
const vigenciaFin = ref<number | null>(null)
const acuerdo = ref<string>('')

function agregarTipoTemporal() {
  const nombre = nuevoTipoNombre.value.trim()
  if (!nombre) return
  if (tiposUnicos.value.some(t => t.tipo === nombre) || tiposTemporal.value.includes(nombre)) {
    mostrarMensaje('Este tipo de modelo ya existe.', 'error')
    return
  }
  tiposTemporal.value.push(nombre)
  tipoSeleccionado.value = nombre
  nuevoTipoNombre.value = ''
  mostrarInputNuevoTipo.value = false
}


function mostrarMensaje(texto: string, tipo: 'positivo' | 'error') {
  mensajeTexto.value = texto
  mensajeTipo.value = tipo
  mensajeVisible.value = true
}

const tiposUnicos = computed(() => {
  const seen = new Set<string>()
  const result: { tipo: string }[] = []

  for (const t of tipoModeloStore.tipos) {
    if (!seen.has(t.tipo)) {
      seen.add(t.tipo)
      result.push({ tipo: t.tipo })
    }
  }

  return result
})

onMounted(async () => {
  await tipoModeloStore.getTipoModelos()
})

// Ajusta el array niveles a la nueva cantidad
const generarNiveles = (): void => {
  const n = Math.floor(Number(cantidadNiveles.value || 0))
  if (!n || n < 1) {
    niveles.value = []
    return
  }
  const prev = niveles.value || []
  niveles.value = Array.from({ length: n }, (_, i) => prev[i] ?? '')
}

// Valida el rango de vigencia con las mismas reglas del backend.
// Devuelve el mensaje de error, o null si es válido (incluye "sin registrar").
function validarVigencia(): string | null {
  const inicio = vigenciaInicio.value
  const fin = vigenciaFin.value

  if (inicio == null && fin == null) return null
  if (inicio == null || fin == null) {
    return 'La vigencia requiere el año inicial y el año final.'
  }
  if (!Number.isInteger(inicio) || !Number.isInteger(fin)) {
    return 'Los años de vigencia deben ser números enteros.'
  }
  if (inicio < 1900 || inicio > 2200 || fin < 1900 || fin > 2200) {
    return 'Los años de vigencia deben estar entre 1900 y 2200.'
  }
  if (fin < inicio) {
    return 'El año final de la vigencia no puede ser anterior al año inicial.'
  }
  return null
}

// Obtener la fecha actual
function hoyFormatoISODate(): string {
  const d = new Date()
  const yyyy = d.getFullYear()
  const mm = String(d.getMonth() + 1).padStart(2, '0')
  const dd = String(d.getDate()).padStart(2, '0')
  return `${yyyy}-${mm}-${dd}`
}

// Enviar modelo
const sendModelo = async () => {
  feedback.value = ''
  
  if (!tipoSeleccionado.value) {
    feedback.value = 'Debe seleccionar un tipo de modelo.'
    return
  }

  if (!nombreModelo.value?.trim()) {
    feedback.value = 'El nombre del modelo es obligatorio.'
    return
  }

  if (!cantidadNiveles.value || cantidadNiveles.value < 1) {
    feedback.value = 'La cantidad de niveles es obligatoria y debe ser ≥ 1.'
    return
  }

  if (!fechaCreacion.value) {
    feedback.value = 'La fecha de creación es obligatoria.'
    return
  }

  const errorVigencia = validarVigencia()
  if (errorVigencia) {
    feedback.value = errorVigencia
    return
  }

  const nombresNiveles = niveles.value.map(n => n.trim()).filter(n => n !== '')
  if (nombresNiveles.length !== cantidadNiveles.value) {
    feedback.value = 'Debes completar el nombre de todos los niveles.'
    return
  }

  // Si el tipo es temporal, crearlo primero
  let tipoEncontrado
  const esTipoTemporal = tiposTemporal.value.includes(tipoSeleccionado.value)

  if (esTipoTemporal) {
    const nuevoTipo = await tipoModeloStore.crearTipoModelo({ tipo: tipoSeleccionado.value })
    if (!nuevoTipo) {
      feedback.value = 'Error al crear el nuevo tipo de modelo.'
      return
    }
    tipoEncontrado = nuevoTipo
    tiposTemporal.value = tiposTemporal.value.filter(t => t !== tipoSeleccionado.value)
  } else {
    tipoEncontrado = tipoModeloStore.tipos.find(t => t.tipo === tipoSeleccionado.value)
  }

  if (!tipoEncontrado?.id_tipo_modelo) {
    feedback.value = 'Tipo de modelo inválido.'
    return
  }

  try {
    loading.value = true

    const modeloPayload: Omit<EstructuraModelo, 'id_modelo'> = {
      nombre: nombreModelo.value.trim(),
      niveles: nombresNiveles,
      estado: 'edicion',
      fecha_creacion: fechaCreacion.value,
      id_tipo_modelo: tipoEncontrado.id_tipo_modelo,
      escala_max: escalaMax.value,
      vigencia_inicio: vigenciaInicio.value ?? null,
      vigencia_fin: vigenciaFin.value ?? null,
      acuerdo: acuerdo.value.trim() || null,
    }

    const result = await store.crearModeloConRaiz(modeloPayload)
    if (!result.ok) {
      mostrarMensaje('Error al guardar Modelo', 'error')
      return
    }
    submitted.value = true
    mostrarMensaje('Modelo creado con éxito', 'positivo')
    setTimeout(() => { router.push({ name: 'ListaVersiones' }) }, 2500)
  } catch (err: any) {
    console.error(err)
    mostrarMensaje(err?.message || 'Error al crear el modelo', 'error')
  } finally {
    loading.value = false
  }
}

return (_ctx: any,_cache: any) => {
  const _component_router_link = _resolveComponent("router-link")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _cache[25] || (_cache[25] = _createElementVNode("h1", null, "Gestión de Modelo", -1)),
    _createElementVNode("div", _hoisted_2, [
      _cache[14] || (_cache[14] = _createElementVNode("label", { for: "tipoModelo" }, "Tipo de Modelo", -1)),
      _createElementVNode("div", _hoisted_3, [
        _withDirectives(_createElementVNode("select", {
          id: "tipoModelo",
          "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((tipoSeleccionado).value = $event))
        }, [
          _cache[13] || (_cache[13] = _createElementVNode("option", {
            disabled: "",
            value: ""
          }, "Seleccione un tipo", -1)),
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(tiposUnicos.value, (t) => {
            return (_openBlock(), _createElementBlock("option", {
              key: t.tipo,
              value: t.tipo
            }, _toDisplayString(t.tipo), 9, _hoisted_4))
          }), 128)),
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(tiposTemporal.value, (t) => {
            return (_openBlock(), _createElementBlock("option", {
              key: 'tmp-' + t,
              value: t
            }, _toDisplayString(t) + " (nuevo) ", 9, _hoisted_5))
          }), 128))
        ], 512), [
          [_vModelSelect, tipoSeleccionado.value]
        ]),
        (!mostrarInputNuevoTipo.value)
          ? (_openBlock(), _createElementBlock("button", {
              key: 0,
              type: "button",
              class: "btn guardar btn-agregar",
              onClick: _cache[1] || (_cache[1] = ($event: any) => (mostrarInputNuevoTipo.value = true))
            }, "+ Agregar tipo"))
          : _createCommentVNode("", true)
      ]),
      (mostrarInputNuevoTipo.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_6, [
            _withDirectives(_createElementVNode("input", {
              type: "text",
              "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((nuevoTipoNombre).value = $event)),
              placeholder: "Nombre del nuevo tipo"
            }, null, 512), [
              [_vModelText, nuevoTipoNombre.value]
            ]),
            _createElementVNode("button", {
              type: "button",
              class: "btn guardar btn-agregar",
              onClick: agregarTipoTemporal
            }, "Agregar"),
            _createElementVNode("button", {
              type: "button",
              class: "btn salir btn-agregar",
              onClick: _cache[3] || (_cache[3] = ($event: any) => {mostrarInputNuevoTipo.value = false; nuevoTipoNombre.value = ''})
            }, "Cancelar")
          ]))
        : _createCommentVNode("", true)
    ]),
    _createElementVNode("div", _hoisted_7, [
      _cache[15] || (_cache[15] = _createElementVNode("label", { for: "nombreModelo" }, "Nombre del Modelo", -1)),
      _withDirectives(_createElementVNode("input", {
        id: "nombreModelo",
        type: "text",
        "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((nombreModelo).value = $event))
      }, null, 512), [
        [_vModelText, nombreModelo.value]
      ])
    ]),
    _createElementVNode("div", _hoisted_8, [
      _cache[16] || (_cache[16] = _createElementVNode("label", { for: "fechaCreacion" }, "Fecha de creación", -1)),
      _withDirectives(_createElementVNode("input", {
        id: "fechaCreacion",
        type: "date",
        "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((fechaCreacion).value = $event)),
        max: fechaMaxima.value
      }, null, 8, _hoisted_9), [
        [_vModelText, fechaCreacion.value]
      ])
    ]),
    _createElementVNode("div", _hoisted_10, [
      _cache[18] || (_cache[18] = _createElementVNode("label", { for: "vigenciaInicio" }, "Vigencia del modelo", -1)),
      _createElementVNode("div", _hoisted_11, [
        _withDirectives(_createElementVNode("input", {
          id: "vigenciaInicio",
          type: "number",
          min: "1900",
          max: "2200",
          step: "1",
          placeholder: "2020",
          "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event: any) => ((vigenciaInicio).value = $event))
        }, null, 512), [
          [
            _vModelText,
            vigenciaInicio.value,
            void 0,
            { number: true }
          ]
        ]),
        _cache[17] || (_cache[17] = _createElementVNode("span", { class: "rango-separador" }, "—", -1)),
        _withDirectives(_createElementVNode("input", {
          id: "vigenciaFin",
          type: "number",
          min: "1900",
          max: "2200",
          step: "1",
          placeholder: "2025",
          "onUpdate:modelValue": _cache[7] || (_cache[7] = ($event: any) => ((vigenciaFin).value = $event))
        }, null, 512), [
          [
            _vModelText,
            vigenciaFin.value,
            void 0,
            { number: true }
          ]
        ])
      ]),
      _cache[19] || (_cache[19] = _createElementVNode("small", { class: "ayuda" }, "Años de inicio y fin de vigencia (ej. 2020-2025).", -1))
    ]),
    _createElementVNode("div", _hoisted_12, [
      _cache[20] || (_cache[20] = _createElementVNode("label", { for: "acuerdo" }, "Acuerdo", -1)),
      _withDirectives(_createElementVNode("input", {
        id: "acuerdo",
        type: "text",
        maxlength: "150",
        placeholder: "Ejemplo: Acuerdo 02 de 2005",
        "onUpdate:modelValue": _cache[8] || (_cache[8] = ($event: any) => ((acuerdo).value = $event))
      }, null, 512), [
        [_vModelText, acuerdo.value]
      ]),
      _cache[21] || (_cache[21] = _createElementVNode("small", { class: "ayuda" }, "Acto administrativo que adopta el modelo.", -1))
    ]),
    _createElementVNode("div", _hoisted_13, [
      _cache[22] || (_cache[22] = _createElementVNode("label", null, "Escala de calificación", -1)),
      _createElementVNode("div", _hoisted_14, [
        _createElementVNode("button", {
          type: "button",
          class: _normalizeClass(['escala-opcion', escalaMax.value === 5 ? 'activa' : '']),
          onClick: _cache[9] || (_cache[9] = ($event: any) => (escalaMax.value = 5))
        }, "0 a 5", 2),
        _createElementVNode("button", {
          type: "button",
          class: _normalizeClass(['escala-opcion', escalaMax.value === 10 ? 'activa' : '']),
          onClick: _cache[10] || (_cache[10] = ($event: any) => (escalaMax.value = 10))
        }, "1 a 10", 2)
      ])
    ]),
    _createElementVNode("div", _hoisted_15, [
      _cache[23] || (_cache[23] = _createElementVNode("label", { for: "cantidadNiveles" }, "Niveles", -1)),
      _withDirectives(_createElementVNode("input", {
        id: "cantidadNiveles",
        type: "number",
        min: "1",
        placeholder: "Ejemplo: 3",
        "onUpdate:modelValue": _cache[11] || (_cache[11] = ($event: any) => ((cantidadNiveles).value = $event)),
        onInput: generarNiveles
      }, null, 544), [
        [
          _vModelText,
          cantidadNiveles.value,
          void 0,
          { number: true }
        ]
      ])
    ]),
    _cache[26] || (_cache[26] = _createElementVNode("div", { class: "divider" }, null, -1)),
    (niveles.value.length > 0)
      ? (_openBlock(), _createElementBlock("div", _hoisted_16, [
          _cache[24] || (_cache[24] = _createElementVNode("h3", null, "Nombre los Niveles", -1)),
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(niveles.value, (nombre, index) => {
            return (_openBlock(), _createElementBlock("div", {
              key: index,
              class: "form-group"
            }, [
              _createElementVNode("label", {
                for: 'nivel-' + index
              }, "Nivel " + _toDisplayString(index + 1), 9, _hoisted_17),
              _withDirectives(_createElementVNode("input", {
                id: 'nivel-' + index,
                type: "text",
                placeholder: "Nombre del Nivel",
                "onUpdate:modelValue": ($event: any) => ((niveles.value[index]) = $event)
              }, null, 8, _hoisted_18), [
                [_vModelText, niveles.value[index]]
              ])
            ]))
          }), 128))
        ]))
      : _createCommentVNode("", true),
    _createElementVNode("div", _hoisted_19, [
      _createVNode(_component_router_link, { to: { name: 'Inicio' } }, {
        default: _withCtx(() => [
          _createElementVNode("button", {
            class: "btn salir",
            disabled: loading.value || submitted.value
          }, "Salir", 8, _hoisted_20)
        ]),
        _: 1
      }),
      _createElementVNode("button", {
        class: "btn guardar",
        onClick: _withModifiers(sendModelo, ["prevent"]),
        disabled: loading.value || submitted.value
      }, _toDisplayString(loading.value ? 'Creando modelo...' : submitted.value ? 'Modelo creado' : 'Guardar'), 9, _hoisted_21)
    ]),
    (feedback.value)
      ? (_openBlock(), _createElementBlock("p", {
          key: 1,
          class: _normalizeClass(['feedback', submitted.value ? 'success' : 'error'])
        }, _toDisplayString(feedback.value), 3))
      : _createCommentVNode("", true),
    _createVNode(MensajeFlotante, {
      visible: mensajeVisible.value,
      mensaje: mensajeTexto.value,
      tipo: mensajeTipo.value,
      onCerrar: _cache[12] || (_cache[12] = ($event: any) => (mensajeVisible.value = false))
    }, null, 8, ["visible", "mensaje", "tipo"])
  ]))
}
}

})